import React from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import ProgressTracker, { ProgressTrackerStep } from '@amzn/meridian/progress-tracker';

interface StageStatus {
    status: string;
    timestamp?: string;
    user?: string;
}

interface ExecutionStatusProps {
    batchId: string | null;
    stages: Record<string, StageStatus>;
}

const STAGE_LABELS: Record<string, string> = {
    cap_setting: 'Cap Setting',
    cycle_transfer: 'Cycle Transfer',
    cap_release: 'Cap Release',
    cap_deployment: 'Cap Deployment',
};

const STAGE_ORDER = ['cap_setting', 'cycle_transfer', 'cap_release', 'cap_deployment'];

const getStepType = (stageKey: string, stages: Record<string, StageStatus>): 'past' | 'present' | 'future' => {
    const status = stages[stageKey]?.status;
    if (status === 'COMPLETED' || status === 'FAILED') return 'past';
    if (status === 'IN_PROGRESS') return 'present';

    const idx = STAGE_ORDER.indexOf(stageKey);
    for (let i = idx + 1; i < STAGE_ORDER.length; i++) {
        const laterStatus = stages[STAGE_ORDER[i]]?.status;
        if (laterStatus && laterStatus !== '') return 'past';
    }
    return 'future';
};

const getAlert = (stageKey: string, stages: Record<string, StageStatus>): 'error' | undefined => {
    return stages[stageKey]?.status === 'FAILED' ? 'error' : undefined;
};

const ExecutionStatus: React.FC<ExecutionStatusProps> = ({ batchId, stages }) => {
    if (!batchId) return null;

    const completedCount = STAGE_ORDER.filter(k => stages[k]?.status === 'COMPLETED').length;
    const failedCount = STAGE_ORDER.filter(k => stages[k]?.status === 'FAILED').length;
    const currentStage = STAGE_ORDER.find(k => stages[k]?.status === 'IN_PROGRESS');
    const timestamps = STAGE_ORDER.map(k => stages[k]?.timestamp).filter(Boolean).sort();
    const batchStartTime = timestamps[0] || '';

    return (
        <Row width="100%" widths="fill" wrap="none" alignmentHorizontal="justify" alignmentVertical="stretch">
            <Column spacing="300" heights="fill" alignmentVertical="justify">
                <Row widths="fill" alignmentHorizontal="justify">
                    <Column spacing="100">
                        <Text type="b200" color="secondary">Current Batch</Text>
                        <Text type="b100">{batchId}</Text>
                    </Column>
                    <Column spacing="100">
                        <Text type="b200" color="secondary">Batch Start Time</Text>
                        <Text type="b100">{batchStartTime ? new Date(batchStartTime).toLocaleString() : '—'}</Text>
                    </Column>
                </Row>
                <Row widths="fill" alignmentHorizontal="justify">
                    <Column spacing="100">
                        <Text type="b200" color="secondary">Current Stage</Text>
                        <Text type="b100">{currentStage ? STAGE_LABELS[currentStage] : 'None'}</Text>
                    </Column>
                    <Column spacing="100">
                        <Text type="b200" color="secondary">Progress</Text>
                        <Text type="b100">{completedCount + failedCount} of {STAGE_ORDER.length} steps complete</Text>
                    </Column>
                </Row>
            </Column>
            <ProgressTracker direction="row" autoTrackLength={true}>
                {STAGE_ORDER.map(key => {
                    const stage = stages[key];
                    const detail = stage?.timestamp && stage?.user
                        ? `Completed at ${new Date(stage.timestamp).toLocaleString()} by ${stage.user}`
                        : stage?.status === 'IN_PROGRESS' ? 'In progress...'
                        : undefined;
                    return (
                        <ProgressTrackerStep
                            key={key}
                            type={getStepType(key, stages)}
                            label={STAGE_LABELS[key]}
                            showLabel={true}
                            alert={getAlert(key, stages)}
                        >
                            {detail && <Text type="b100" color="secondary">{detail}</Text>}
                        </ProgressTrackerStep>
                    );
                })}
            </ProgressTracker>
        </Row>
    );
};

export default ExecutionStatus;
